import { useState, useRef } from "react";
import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Camera, Upload, Check, AlertCircle, Loader2, FileText, Plus, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DOCUMENT_ACCEPT, DOCUMENT_ACCEPT_LABEL } from "@/lib/upload-accept";
import { WARM } from "@shared/brand";

interface UploadLinkInfo {
  token: string;
  targetType: "checklist_item" | "digital_asset" | "generic_section";
  targetId: string;
  targetInfo: {
    name?: string;
    category?: string;
    accountType?: string;
    sectionName?: string;
    fieldNames?: string[];
    fieldLabel?: string;
    rowLabel?: string;
    rowId?: string;
  };
  clientName?: string;
  expiresAt: string;
}

// "queued" (session 90): extraction runs asynchronously on the server — the phone
// can't report a field count, so it tells the person to review on their computer.
export type ExtractionOutcome = "queued" | "fields_found" | "no_fields" | "error";

export interface UploadRecord {
  filename: string;
  extractedCount: number;
  /** Extraction outcome from the endpoint (generic_section only). */
  outcome?: ExtractionOutcome;
  /** True = extracted values are staged for desktop review, not yet saved. */
  pending?: boolean;
}

/**
 * Maps an upload record to its terminal-state presentation. Content-agnostic —
 * works for any generic_section, no per-section copy. Distinguishes a genuine
 * empty result ("no_fields") from a failed extraction ("error") so the person
 * on the phone can tell "nothing on the document" from "something went wrong",
 * and words a successful extraction honestly (pending desktop review vs saved).
 */
export function uploadOutcomeView(
  f: UploadRecord,
  isGenericSection: boolean,
): { icon: React.ReactNode; containerClass: string; title: string; sub?: string } {
  const success = "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800";
  const neutral = "bg-muted/40 border-border";

  // Non-extracting uploads (checklist) have no outcome.
  if (!isGenericSection || !f.outcome) {
    return {
      icon: <Check className="h-5 w-5 text-green-600 shrink-0" />,
      containerClass: success,
      title: "Uploaded",
      sub: "Your document has been attached.",
    };
  }

  switch (f.outcome) {
    case "queued":
      return {
        icon: <Check className="h-5 w-5 text-green-600 shrink-0" />,
        containerClass: success,
        title: "Uploaded",
        sub: "We're reading your document. Open the app on your computer to review and save the details.",
      };
    case "fields_found": {
      const noun = `${f.extractedCount} detail${f.extractedCount !== 1 ? "s" : ""}`;
      return f.pending
        ? {
            icon: <Check className="h-5 w-5 text-green-600 shrink-0" />,
            containerClass: success,
            title: `${noun} found`,
            sub: "Open the app on your computer to review and save them.",
          }
        : {
            icon: <Check className="h-5 w-5 text-green-600 shrink-0" />,
            containerClass: success,
            title: `${noun} filled in`,
            sub: "Saved to your plan.",
          };
    }
    case "error":
      return {
        icon: <AlertCircle className="h-5 w-5 text-destructive shrink-0" />,
        containerClass: neutral,
        title: "We couldn't read this document",
        sub: "It's been attached, but no details were filled in. You can try another photo.",
      };
    case "no_fields":
    default:
      return {
        icon: <FileText className="h-5 w-5 text-muted-foreground shrink-0" />,
        containerClass: neutral,
        title: "No details found",
        sub: "Your document has been attached.",
      };
  }
}

export default function MobileUploadPage() {
  const [, params] = useRoute("/upload/:token");
  const token = params?.token;
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadRecord[]>([]);
  const [sessionDone, setSessionDone] = useState(false);
  // UPLOAD-CANCEL-1 (s169): the recipient backed out without uploading. Purely local — the
  // link is NOT invalidated (token-kill is a separate, deferred decision), so the terminal
  // copy below must not imply the link is dead. Reachable only before the first upload.
  const [cancelled, setCancelled] = useState(false);

  const { data: linkInfo, isLoading, error } = useQuery<UploadLinkInfo>({
    queryKey: ["/api/upload-links", token],
    enabled: !!token,
  });

  const isGenericSection = linkInfo?.targetType === "generic_section";

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) return;
    setUploading(true);
    if (isGenericSection) setAnalyzing(true);

    try {
      const reader = new FileReader();
      const base64Data = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const res = await fetch(`/api/upload-links/${token}/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: selectedFile.name,
          contentType: selectedFile.type,
          data: base64Data,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Upload failed");
      }

      const result = await res.json();
      setUploadedFiles(prev => [...prev, {
        filename: selectedFile.name,
        extractedCount: 0,
        // Async (session 90): a generic_section upload returns `extractionQueued`;
        // extraction completes in the background and surfaces on the desktop app.
        outcome: result.extractionQueued ? "queued" as ExtractionOutcome : undefined,
        pending: false,
      }]);
      setSelectedFile(null);

      if (!isGenericSection) {
        setSessionDone(true);
      }
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-10 flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading upload link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Error state ──
  if (error || !linkInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-10 flex flex-col items-center gap-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Link Not Found</h2>
            <p className="text-muted-foreground text-center">
              This upload link is invalid, expired, or has already been used.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Cancelled (UPLOAD-CANCEL-1, s169) ──
  // A recipient who opened the link by accident, or on the wrong device, previously had no way
  // out but to close the tab and wonder whether something had happened. This states plainly that
  // nothing was sent AND that the link still works — because it does; no token was invalidated.
  if (cancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-10 flex flex-col items-center gap-4" data-testid="upload-cancelled">
            <div className="rounded-full bg-muted p-4">
              <X className="h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold">Nothing was uploaded</h2>
            <p className="text-muted-foreground text-center">
              You can close this window. Your link still works until it expires, so you can come
              back and upload later if you'd like.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Session done (non-generic types after first upload, or explicit done) ──
  if (sessionDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-10 flex flex-col items-center gap-4">
            <div className="rounded-full bg-green-100 dark:bg-green-900 p-4">
              <Check className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-semibold">All Done!</h2>
            {uploadedFiles.length > 0 && isGenericSection && (
              <p className="text-muted-foreground text-center text-sm">
                {uploadedFiles.length} document{uploadedFiles.length !== 1 ? 's' : ''} uploaded — review the details on your computer.
              </p>
            )}
            <p className="text-muted-foreground text-center">
              You can close this page and return to the app.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const targetDescription =
    linkInfo.targetType === "generic_section"
      ? linkInfo.targetInfo.sectionName || "Section"
      : linkInfo.targetType === "checklist_item"
      ? linkInfo.targetInfo.name || "Checklist item"
      : linkInfo.targetInfo.accountType || "Digital asset";

  const fieldNames = linkInfo.targetInfo.fieldNames ?? [];
  const fieldLabel = linkInfo.targetInfo.fieldLabel;
  const rowLabel = linkInfo.targetInfo.rowLabel;

  // ── Analyzing state ──
  if (analyzing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-10 flex flex-col items-center gap-6">
            <div className="relative">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-xl font-semibold">Your document is being analyzed...</h2>
              <p className="text-sm text-muted-foreground">
                AI is reading your photo and filling in the fields. This takes a few seconds.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <CardTitle>Upload Document</CardTitle>
          <CardDescription>
            For: <span className="font-medium text-foreground">{targetDescription}</span>
            {linkInfo.clientName && (
              <span className="block mt-1 text-xs">Client: {linkInfo.clientName}</span>
            )}
          </CardDescription>
          {isGenericSection && rowLabel && (
            <p className="text-xs text-muted-foreground mt-2">
              Uploading document for: <span className="font-medium text-foreground">{rowLabel}</span>
            </p>
          )}
          {isGenericSection && !rowLabel && fieldLabel && (
            <p className="text-xs text-muted-foreground mt-2">
              You are uploading a document for: <span className="font-medium text-foreground">{fieldLabel}</span>
            </p>
          )}
          {isGenericSection && !rowLabel && !fieldLabel && fieldNames.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              AI will try to fill: {fieldNames.join(", ")}
            </p>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Hidden inputs */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={DOCUMENT_ACCEPT}
            onChange={handleFileSelect}
          />
          <input
            ref={cameraInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
          />

          {/* Outcome of each upload this session — a clear terminal state per
              file (found / no-details / error), not a row the user can miss. */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              {uploadedFiles.map((f, i) => {
                const view = uploadOutcomeView(f, isGenericSection);
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${view.containerClass}`}
                  >
                    {view.icon}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{view.title}</p>
                      {view.sub && <p className="text-xs text-muted-foreground mt-0.5">{view.sub}</p>}
                      <p className="text-xs text-muted-foreground/80 truncate mt-1">{f.filename}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!selectedFile ? (
            <div className="space-y-3">
              {/* Primary: Take Photo */}
              <button
                type="button"
                className="w-full flex flex-col items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-4 py-8 text-base font-semibold hover:bg-primary/90 active:scale-95 transition-all"
                onClick={() => cameraInputRef.current?.click()}
                data-testid="button-take-photo"
              >
                <Camera className="h-10 w-10" />
                <span>{uploadedFiles.length > 0 ? "Take Another Photo" : "Take Photo"}</span>
              </button>

              {/* Secondary: Upload from Phone */}
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-border bg-background text-foreground px-4 py-4 text-sm font-medium hover:bg-muted transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="button-choose-file"
              >
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span>{uploadedFiles.length > 0 ? "Upload Another from Phone" : "Upload from your Phone"}</span>
              </button>

              {/* s142 (BATCH-UPLOAD-FAIL-1 #4): file-type guidance */}
              <p className="text-center text-xs" style={{ color: WARM.taupe }}>{DOCUMENT_ACCEPT_LABEL}.</p>

              {/* Done button (shown after at least one upload for generic_section) */}
              {uploadedFiles.length > 0 && (
                <button
                  type="button"
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground py-2"
                  onClick={() => setSessionDone(true)}
                >
                  Done — close this page
                </button>
              )}

              {/* UPLOAD-CANCEL-1 (s169): the back-out. Shown ONLY before the first upload —
                  once something has been sent, "Done — close this page" above is the honest
                  exit and a Cancel here would falsely suggest the upload could be undone. */}
              {uploadedFiles.length === 0 && (
                <button
                  type="button"
                  // s169 visibility amendment: this shipped as caption-grey text and read as a
                  // disclaimer rather than a control. Secondary-action treatment — foreground
                  // colour, underline, and a full-width 44px-class tap target (this page is
                  // phone-first, so a text-sized hit area is the wrong affordance).
                  className="w-full text-center text-sm font-medium text-foreground underline underline-offset-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setCancelled(true)}
                  data-testid="button-cancel-upload"
                >
                  Cancel — I don't need to upload anything
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <FileText className="h-8 w-8 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                  data-testid="button-change-file"
                >
                  Change
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="button-upload"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    "Upload"
                  )}
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground">
            This link expires on {new Date(linkInfo.expiresAt).toLocaleString()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
